import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Check, X as XIcon, Ban, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const STATUS = { pending: "En attente", approved: "Autorisé", rejected: "Refusé", revoked: "Révoqué" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR") : "—");

// Onglet « Appareils » : demandes de connexion des utilisateurs « Autoriser sur une machine »
// (voir device_utils.py). L'administrateur valide ou refuse chaque nouvel appareil.
export default function DevicesTab({ onChange }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/devices", { params: filter ? { status: filter } : {} });
      setItems(data);
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (id, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      if (action === "delete") await api.delete(`/devices/${id}`);
      else if (action === "approve") {
        const name = window.prompt("Nom de cet appareil (facultatif, ex. « Caisse comptoir ») :", "") || "";
        await api.post(`/devices/${id}/approve`, { name });
      } else await api.post(`/devices/${id}/${action}`);
      toast.success("Enregistré");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="devices-tab">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold mr-2">Appareils des utilisateurs « Autoriser sur une machine »</h3>
        {[["pending", "En attente"], ["approved", "Autorisés"], ["", "Tous"]].map(([k, label]) => (
          <Button key={k || "all"} size="sm" variant={filter === k ? "default" : "outline"} onClick={() => setFilter(k)} data-testid={`devices-filter-${k || "all"}`}>{label}</Button>
        ))}
        <Button size="sm" variant="ghost" onClick={load} disabled={loading} title="Actualiser"><RefreshCw size={14} /></Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Un utilisateur limité à une machine ne peut se connecter que depuis un appareil validé ici. Révoquer un appareil coupe immédiatement sa session.
      </p>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="devices-empty">{loading ? "Chargement…" : "Aucun appareil dans cette liste."}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground"><tr>
              <th className="p-3">Utilisateur</th><th className="p-3">Appareil</th><th className="p-3">IP</th><th className="p-3">Demandé le</th><th className="p-3">Dernière connexion</th><th className="p-3">État</th><th className="p-3" />
            </tr></thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} className="border-t border-border" data-testid={`device-row-${d.id}`}>
                  <td className="p-3">{d.user_nom}<span className="block text-xs text-muted-foreground font-mono">{d.matricule}</span></td>
                  <td className="p-3 max-w-xs">{d.name && <strong className="block">{d.name}</strong>}<span className="text-xs text-muted-foreground break-words">{d.label}</span></td>
                  <td className="p-3 font-mono text-xs">{d.ip || "—"}</td>
                  <td className="p-3 text-xs">{fmt(d.created_at)}</td>
                  <td className="p-3 text-xs">{fmt(d.last_seen)}{d.last_ip ? <span className="block font-mono">{d.last_ip}</span> : null}</td>
                  <td className="p-3"><span className="text-xs">{STATUS[d.status] || d.status}</span></td>
                  <td className="p-3 flex justify-end gap-1.5">
                    {d.status === "pending" && (<>
                      <Button size="sm" onClick={() => act(d.id, "approve")} data-testid={`device-approve-${d.id}`}><Check size={14} className="mr-1" />Autoriser</Button>
                      <Button size="sm" variant="outline" onClick={() => act(d.id, "reject")} data-testid={`device-reject-${d.id}`}><XIcon size={14} className="mr-1" />Refuser</Button>
                    </>)}
                    {d.status === "approved" && (
                      <Button size="sm" variant="outline" onClick={() => act(d.id, "revoke", "Révoquer cet appareil ? La session en cours sera coupée.")} data-testid={`device-revoke-${d.id}`}><Ban size={14} className="mr-1" />Révoquer</Button>
                    )}
                    {d.status !== "approved" && d.status !== "pending" && (
                      <Button size="sm" variant="ghost" onClick={() => act(d.id, "delete")} data-testid={`device-delete-${d.id}`}><Trash2 size={14} /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
